export const parsePositiveId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.trunc(parsed);
};

const buildLocalId = (prefix) => `${prefix}-${Date.now()}-${Math.random()}`;

export const buildTask = (taskIndex = 1, overrides = {}) => ({
  id: null,
  local_id: buildLocalId("task"),
  title: "",
  description: "",
  due_offset_days: 0,
  sort_order: taskIndex,
  is_required: true,
  is_active: true,
  ...overrides,
});

export const buildField = (fieldIndex = 1, overrides = {}) => ({
  id: null,
  local_id: buildLocalId("field"),
  label: "",
  field_type: "url",
  help_text: "",
  is_required: true,
  sort_order: fieldIndex,
  is_active: true,
  option_labels: [],
  ...overrides,
});

export const buildStage = (stageIndex = 1, overrides = {}) => ({
  id: null,
  local_id: buildLocalId("stage"),
  name: "",
  owner_user_id: null,
  sort_order: stageIndex,
  is_terminal: false,
  is_active: true,
  tasks: [buildTask(1)],
  fields: [],
  ...overrides,
});

export const buildCreateForm = () => ({
  product_id: null,
  name: "",
  default_owner_mode: "contract_owner",
  default_owner_user_id: null,
  auto_move_next_stage_on_all_tasks_done: true,
  allow_manual_stage_move_with_open_tasks: true,
  is_active: true,
  general_fields: [],
  stages: [buildStage(1)],
});

export const ONBOARDING_TEMPLATE_FIELD_TYPE_ORDER = Object.freeze([
  { value: "url", icon: "mdi-link-variant" },
  { value: "short_text", icon: "mdi-form-textbox" },
  { value: "long_text", icon: "mdi-text-box-multiple-outline" },
  { value: "number", icon: "mdi-numeric" },
  { value: "phone", icon: "mdi-phone-outline" },
  { value: "email", icon: "mdi-email-outline" },
  { value: "date", icon: "mdi-calendar" },
  { value: "checkbox", icon: "mdi-checkbox-marked-outline" },
  { value: "select", icon: "mdi-form-select" },
]);

export const normalizedOptionLabels = (values) => {
  return (Array.isArray(values) ? values : [])
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);
};

const mapFieldOptionsToLabels = (field) => {
  return (Array.isArray(field?.options) ? field.options : [])
    .map((option) => String(option?.label || "").trim())
    .filter(Boolean);
};

export const mapTemplateDetailsToForm = (payload) => {
  const template = payload?.template || {};
  const templateOwnerUserId = parsePositiveId(template?.default_owner_user_id ?? template?.defaultOwnerUserId);
  const stages = Array.isArray(payload?.stages) ? payload.stages : [];
  const generalFields = Array.isArray(payload?.general_fields) ? payload.general_fields : [];
  const sortedStages = [...stages].sort((left, right) => {
    const leftSort = Number(left?.sort_order) || 0;
    const rightSort = Number(right?.sort_order) || 0;
    if (leftSort !== rightSort) return leftSort - rightSort;
    return (Number(left?.id) || 0) - (Number(right?.id) || 0);
  });

  const formStages = sortedStages.map((stage, stageIndex) => {
    const tasks = Array.isArray(stage?.tasks) ? stage.tasks : [];
    const fields = Array.isArray(stage?.fields) ? stage.fields : [];
    const sortedTasks = [...tasks].sort((left, right) => {
      const leftSort = Number(left?.sort_order) || 0;
      const rightSort = Number(right?.sort_order) || 0;
      if (leftSort !== rightSort) return leftSort - rightSort;
      return (Number(left?.id) || 0) - (Number(right?.id) || 0);
    });
    const sortedFields = [...fields].sort((left, right) => {
      const leftSort = Number(left?.sort_order) || 0;
      const rightSort = Number(right?.sort_order) || 0;
      if (leftSort !== rightSort) return leftSort - rightSort;
      return (Number(left?.id) || 0) - (Number(right?.id) || 0);
    });

    return buildStage(stageIndex + 1, {
      id: parsePositiveId(stage?.id),
      name: String(stage?.name || ""),
      owner_user_id: parsePositiveId(stage?.owner_user_id ?? stage?.ownerUserId) || templateOwnerUserId,
      sort_order: Number(stage?.sort_order) || stageIndex + 1,
      is_terminal: Number(stage?.is_terminal) === 1 || stage?.is_terminal === true,
      is_active: Number(stage?.is_active) === 1 || stage?.is_active === true,
      tasks: sortedTasks.map((task, taskIndex) =>
        buildTask(taskIndex + 1, {
          id: parsePositiveId(task?.id),
          title: String(task?.title || ""),
          description: String(task?.description || ""),
          due_offset_days:
            task?.due_offset_days === null || task?.due_offset_days === undefined
              ? null
              : Number(task.due_offset_days),
          sort_order: Number(task?.sort_order) || taskIndex + 1,
          is_required: Number(task?.is_required) === 1 || task?.is_required === true,
          is_active: Number(task?.is_active) === 1 || task?.is_active === true,
        })
      ),
      fields: sortedFields.map((field, fieldIndex) =>
        buildField(fieldIndex + 1, {
          id: parsePositiveId(field?.id),
          label: String(field?.label || ""),
          field_type: String(field?.field_type || "url"),
          help_text: String(field?.help_text || ""),
          is_required: Number(field?.is_required) === 1 || field?.is_required === true,
          sort_order: Number(field?.sort_order) || fieldIndex + 1,
          is_active: Number(field?.is_active) === 1 || field?.is_active === true,
          option_labels: mapFieldOptionsToLabels(field),
        })
      ),
    });
  });

  return {
    product_id: parsePositiveId(template?.product_id),
    name: String(template?.name || ""),
    default_owner_mode: String(template?.default_owner_mode || "contract_owner"),
    default_owner_user_id: templateOwnerUserId,
    auto_move_next_stage_on_all_tasks_done:
      Number(template?.auto_move_next_stage_on_all_tasks_done) === 1
      || template?.auto_move_next_stage_on_all_tasks_done === true,
    allow_manual_stage_move_with_open_tasks:
      Number(template?.allow_manual_stage_move_with_open_tasks) === 1
      || template?.allow_manual_stage_move_with_open_tasks === true,
    is_active: Number(template?.is_active) === 1 || template?.is_active === true,
    general_fields: generalFields.map((field, fieldIndex) =>
      buildField(fieldIndex + 1, {
        id: parsePositiveId(field?.id),
        label: String(field?.label || ""),
        field_type: String(field?.field_type || "url"),
        help_text: String(field?.help_text || ""),
        is_required: Number(field?.is_required) === 1 || field?.is_required === true,
        sort_order: Number(field?.sort_order) || fieldIndex + 1,
        is_active: Number(field?.is_active) === 1 || field?.is_active === true,
        option_labels: mapFieldOptionsToLabels(field),
      })
    ),
    stages: formStages.length ? formStages : [buildStage(1)],
  };
};

const buildFieldPayload = (field, fieldIndex, includeIds) => {
  const fieldPayload = {
    label: String(field?.label || "").trim(),
    field_type: String(field?.field_type || "url").trim().toLowerCase(),
    help_text: String(field?.help_text || "").trim() || null,
    is_required: field?.is_required !== false,
    sort_order: Number(field?.sort_order) || fieldIndex + 1,
    is_active: field?.is_active !== false,
  };
  if (fieldPayload.field_type === "select") {
    fieldPayload.options = normalizedOptionLabels(field.option_labels).map((label, optionIndex) => ({
      label,
      sort_order: optionIndex + 1,
      is_active: true,
    }));
  }
  if (includeIds) {
    const fieldId = parsePositiveId(field?.id);
    if (fieldId) fieldPayload.id = fieldId;
  }
  return fieldPayload;
};

const buildTaskPayload = (task, taskIndex, includeIds) => {
  const dueOffset = Number(task?.due_offset_days);
  const taskPayload = {
    title: String(task?.title || "").trim(),
    description: String(task?.description || "").trim() || null,
    task_type: "custom",
    owner_mode: "onboarding_owner",
    due_offset_days: Number.isFinite(dueOffset) ? Math.trunc(dueOffset) : null,
    is_required: task?.is_required !== false,
    sort_order: Number(task?.sort_order) || taskIndex + 1,
    is_active: task?.is_active !== false,
  };
  if (includeIds) {
    const taskId = parsePositiveId(task?.id);
    if (taskId) taskPayload.id = taskId;
  }
  return taskPayload;
};

export const buildTemplatePayload = (createForm, { includeIds = false } = {}) => ({
  product_id: parsePositiveId(createForm.product_id),
  name: String(createForm.name || "").trim(),
  default_owner_mode: createForm.default_owner_mode,
  default_owner_user_id: parsePositiveId(createForm.default_owner_user_id),
  auto_move_next_stage_on_all_tasks_done: Boolean(createForm.auto_move_next_stage_on_all_tasks_done),
  allow_manual_stage_move_with_open_tasks: Boolean(createForm.allow_manual_stage_move_with_open_tasks),
  is_active: Boolean(createForm.is_active),
  general_fields: (createForm.general_fields || []).map((field, fieldIndex) =>
    buildFieldPayload(field, fieldIndex, includeIds)
  ),
  stages: (createForm.stages || []).map((stage, stageIndex) => {
    const stagePayload = {
      name: String(stage?.name || "").trim(),
      owner_user_id: parsePositiveId(stage?.owner_user_id),
      sort_order: Number(stage?.sort_order) || stageIndex + 1,
      is_terminal: Boolean(stage?.is_terminal),
      is_active: stage?.is_active !== false,
      tasks: (stage?.tasks || []).map((task, taskIndex) => buildTaskPayload(task, taskIndex, includeIds)),
      fields: (stage?.fields || []).map((field, fieldIndex) => buildFieldPayload(field, fieldIndex, includeIds)),
    };
    if (includeIds) {
      const stageId = parsePositiveId(stage?.id);
      if (stageId) stagePayload.id = stageId;
    }
    return stagePayload;
  }),
});

export const isTemplateFormValid = (createForm) => {
  if (!parsePositiveId(createForm?.product_id)) return false;
  if (!String(createForm?.name || "").trim()) return false;
  if (!parsePositiveId(createForm?.default_owner_user_id)) return false;
  if (!Array.isArray(createForm?.stages) || !createForm.stages.length) return false;

  const areFieldsValid = (fields) =>
    (Array.isArray(fields) ? fields : []).every((field) => {
      if (!String(field?.label || "").trim()) return false;
      if (String(field?.field_type || "").trim().toLowerCase() === "select") {
        return normalizedOptionLabels(field.option_labels).length > 0;
      }
      return true;
    });

  if (!areFieldsValid(createForm.general_fields)) return false;
  return createForm.stages.every((stage) => {
    if (!String(stage?.name || "").trim()) return false;
    if (!parsePositiveId(stage?.owner_user_id)) return false;
    const tasks = Array.isArray(stage?.tasks) ? stage.tasks : [];
    const fields = Array.isArray(stage?.fields) ? stage.fields : [];
    if (!tasks.length && !fields.length) return false;
    if (!tasks.every((task) => String(task?.title || "").trim())) return false;
    return areFieldsValid(fields);
  });
};
